class MainMenu extends HTMLElement {
  constructor() {
    super();
    this.options = {
      onHover: true,
      hoverDelay: 300,
      closeOnMouseleave: true,
      toggleClass: 'open',
      closeSiblings: true,
      closeChildren: true,
    };
  }

  connectedCallback() {
    // Reduce actions (So the event can also be removed)
    this.reducer = {
      clickTarget: (e) => this.debounceClickEvent(e),
      mouseEnterTarget: (e) => this.debouncedOnMouse(e, 'open'),
      mouseLeaveGroup: (e) => this.debouncedOnMouse(e, 'close'),
      mouseLeaveContainer: (e) => this.debouncedOnMouse(e, 'closeAll'),
    };

    this.groups = this.hasAttribute('data-main-menu="group"')
      ? [this.closest('[data-main-menu="group"]')]
      : this.querySelectorAll('[data-main-menu="group"]');
    this.level_1_items = this.querySelectorAll('[data-main-menu-group-level-1]');
    this.triggers = this.querySelectorAll('[data-main-menu="trigger"]');

    this.breakpoint = window.matchMedia('(min-width: 1024px)');
    const handlehandleEvents = () => this.setEvents();
    this.breakpoint.addEventListener('change', () => {
      handlehandleEvents();
    });
    handlehandleEvents();

    this.timeout = null;
    this.header = this.closest('[data-header]');
    if (this.header) {
      // on key up
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });
    }

    // Trigger mouseleave on container <main-menu>
    if (this.options.closeOnMouseleave) {
      this.removeEventListener('mouseleave', this.reducer.mouseLeaveContainer);
      this.addEventListener('mouseleave', this.reducer.mouseLeaveContainer);
    }
  }

  setEvents() {
    // Trigger events on [data-main-menu="trigger"]
    this.triggers.forEach((trigger) => {
      trigger.removeEventListener('click', this.reducer.clickTarget);
      trigger.addEventListener('click', this.reducer.clickTarget);

      // Mouse enter
      if (this.options.onHover && this.breakpoint.matches) {
        trigger.removeEventListener('mouseenter', this.reducer.mouseEnterTarget);
        trigger.addEventListener('mouseenter', this.reducer.mouseEnterTarget);
      }
    });

    this.level_1_items.forEach((level_1_item) => {
      if (this.options.onHover && this.breakpoint.matches) {
        level_1_item.addEventListener('mouseenter', () => {
          this.closeSiblings(level_1_item);
        });
      }
    });
  }

  /*
    Debounce click event
    @param e {object}: mouseevent
    Called by add event listener -> reducer.
  */
  debounceClickEvent(e) {
    if (!e) {
      return false;
    }
    const buttonType = e.currentTarget.nodeName.toLowerCase();
    const group = e.currentTarget.closest('[data-main-menu="group"]');
    // only toggle if is closed or target is not an <a> tag
    if (!group.classList.contains(this.options.toggleClass) || buttonType != 'a') {
      e.preventDefault();
      this.toggle(group);
    }
  }

  /*
    Debounced mouse event
    @param e {object}: mouseevent
    @param type {string}: open/close/closeAll
    Called by add event listener -> reducer.
  */
  debouncedOnMouse(e, type) {
    if (!e) return false;

    // clear old trigger
    clearTimeout(this.timeout);

    // add new trigger
    this.timeout = setTimeout(() => {
      const group = e?.target?.closest('[data-main-menu="group"]');
      switch (type) {
        case 'open':
          this.open(group);
          break;
        case 'close':
          this.close(group);
          break;
        case 'closeAll':
          this.closeAll();
          break;
        default:
          break;
      }
    }, this.options.hoverDelay);
  }

  /*
    Open collapsible group
    @param group {node}: group selector
  */
  open(group) {
    if (!group) {
      return false;
    }

    // Close sibblings if option is on
    this.options.closeSiblings && this.closeSiblings(group);

    // Open active group
    group.classList.add(this.options.toggleClass);
    group.querySelector('[data-main-menu="trigger"]')?.setAttribute('aria-expanded', 'true');
  }

  /*
    close collapsible group,
    @param group {node}: group selector
  */
  close(group) {
    if (!group) {
      return false;
    }
    // Close active group
    group.classList.remove(this.options.toggleClass);
    group.querySelector('[data-main-menu="trigger"]')?.setAttribute('aria-expanded', 'false');

    // Close child collapsibles
    if (this.options.closeChildren) {
      group.querySelectorAll('[data-main-menu="group"]').forEach((group) => {
        this.close(group);
      });
    }
  }

  /*
    Toggle controller, open / close collapsibles
    @param group {node}: group selector
  */
  toggle(group) {
    if (!group) {
      return false;
    }

    // Check if already open
    if (!group.classList.contains(this.options.toggleClass)) {
      // Open
      this.open(group);
    } else {
      // Close
      this.close(group);
    }
  }

  /*
    Close all sibling collapsibles
    @param currentGroup {node}: current group selector
  */
  closeSiblings(currentGroup = null) {
    this.groups.forEach((group) => {
      if (
        group !== currentGroup &&
        !group.contains(currentGroup) &&
        !currentGroup.contains(group)
      ) {
        this.close(group);
      }
    });
  }

  /*
    Open+ all collapsibles
  */
  openAll() {
    this.groups.forEach((group) => {
      group.classList.add(this.options.toggleClass);
    });
  }

  /*
    Close all collapsibles
  */
  closeAll() {
    this.groups.forEach((group) => {
      group.classList.remove(this.options.toggleClass);
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('header-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    let group = this.querySelector(`[data-main-menu="group"].${toggleClass}`);
    if (group) {
      this.close(group);
    }
  }
}
if (!customElements.get('main-menu')) {
  customElements.define('main-menu', MainMenu);
}

class ToggleMobileNavigation extends ToggleElementClass {
  constructor() {
    super();

    this.header = this.closest('[data-header]');

    if (this.header) {
      // Extra events
      document.addEventListener('keyup', (event) => {
        this.onKeyUp(event);
      });
    }
  }

  events() {
    if (!this.header) {
      super.events();
    }
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('main-menu-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    trapFocus(this.header);
  }

  remove() {
    super.remove();
    this.closeAllSiblings();
    removeTrapFocus();
  }

  closeAllSiblings() {
    const collapsibles = this.target.querySelector('main-menu');
    collapsibles.closeAll();
  }
}

if (!customElements.get('toggle-mobile-navigation')) {
  customElements.define('toggle-mobile-navigation', ToggleMobileNavigation);
}

class ToggleSearch extends ToggleElementClass {
  constructor() {
    super();
  }

  construct() {
    super.construct();

    // elements
    this.header = this.closest('[data-header]');

    // options
    const { toggleClass, target } = this.options;

    // key up escape
    document.addEventListener('keyup', (event) => {
      this.onKeyUp(event);
    });

    // Remove classes in case the header navigation drawer is open.
    this.addEventListener('click', () => {
      document.body.classList.remove('header-is-open');
      document.body.classList.remove('main-menu-open');
      // Focus on input element if provided
      if (this.options.focusTarget) {
        document.querySelector(this.options.focusTarget)?.focus();
      }
    });
  }

  onKeyUp(event) {
    if (!document.body.classList.contains('ps-is-open')) {
      return;
    }
    if (event?.code.toUpperCase() !== 'ESCAPE') {
      return;
    }
    const { toggleClass } = this.options;
    if (this.target?.classList.contains(toggleClass)) {
      this.remove();
    }
  }

  add() {
    super.add();
    if (this.header) {
      document.body.classList.add('ps-is-open');
      document.querySelector('predictive-search').querySelector('input')?.focus();
    }
    if (this.target) {
      trapFocus(this.target);
    }
  }

  remove() {
    super.remove();
    if (this.header) {
      document.body.classList.remove('ps-is-open');
    }
    removeTrapFocus();
  }
}

if (!customElements.get('toggle-search')) {
  customElements.define('toggle-search', ToggleSearch);
}
